import React, { useState } from 'react';
import { Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Waitlist email-capture form. Owns its own form state to keep the
 * LandingPage shell lean. Submits to /api/mailchimp/subscribe.
 */
export default function WaitlistForm({ showBetaMessage }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [volume, setVolume] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API}/api/mailchimp/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, volume }),
      });
      const data = await response.json();
      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setName('');
        setVolume('');
      } else {
        if (process.env.NODE_ENV !== 'production') console.error('Subscription error:', data);
        setSubmitStatus('error');
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('Network error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#00D26A]/10 via-[#050505] to-[#FFD700]/10 text-white relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#00D26A]/20 blur-[150px] rounded-full"></div>

      <div className="max-w-2xl mx-auto relative z-10">
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {showBetaMessage ? '🔒 Beta Access Required - Join Waitlist' : 'Join the Waitlist (Free)'}
        </h2>

        {showBetaMessage && (
          <div className="mb-6 p-4 bg-[#FFD700]/20 border border-[#FFD700]/50 rounded-lg text-center">
            <p className="text-white">
              <strong>Thanks for your interest!</strong> Flipping Friends is currently in private beta.
              Join our waitlist and we'll notify you when spots open up.
            </p>
          </div>
        )}

        <p className="text-center text-zinc-300 mb-8 text-lg leading-relaxed">
          No payment required. We'll email you when beta opens in September 2026.
        </p>

        {submitStatus === 'success' ? (
          <div className="bg-gradient-to-r from-[#00D26A] to-[#FFD700] text-black p-6 rounded-2xl text-center shadow-lg" data-testid="waitlist-success">
            <Check className="w-16 h-16 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">You're on the list!</h3>
            <p>Check your email for confirmation. We'll notify you when beta opens.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" id="waitlist-form" data-testid="waitlist-form">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">Email Address *</label>
              <input
                type="email" id="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#0A0A0A] text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                data-testid="waitlist-email-input"
              />
            </div>
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-2">Name (optional)</label>
              <input
                type="text" id="name" value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="What should we call you?"
                className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#0A0A0A] text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                data-testid="waitlist-name-input"
              />
            </div>
            <div>
              <label htmlFor="volume" className="block text-sm font-medium mb-2">Flipping Volume (optional)</label>
              <select
                id="volume" value={volume}
                onChange={(e) => setVolume(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-zinc-700 bg-[#0A0A0A] text-white focus:outline-none focus:ring-2 focus:ring-[#00D26A]"
                data-testid="waitlist-volume-select"
              >
                <option value="">Select your volume...</option>
                <option value="hobby">10-50 items/month (Home Collection)</option>
                <option value="part-time">50-200 items/month (Part-time)</option>
                <option value="full-time">200-1,000 items/month (Full-time)</option>
                <option value="professional">1,000+ items/month (Professional)</option>
              </select>
            </div>
            <button
              type="submit" disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-[#00D26A] to-[#FFD700] hover:opacity-90 disabled:opacity-50 text-black font-bold px-8 py-4 rounded-full text-lg transition-all transform hover:translate-y-[-2px] shadow-[0_0_30px_rgba(0,210,106,0.3)] disabled:cursor-not-allowed"
              data-testid="waitlist-submit-btn"
            >
              {isSubmitting ? 'Joining...' : "Join Beta Waitlist - It's Free"}
            </button>
            {submitStatus === 'error' && (
              <p className="text-red-300 text-center text-sm" data-testid="waitlist-error">
                Something went wrong. Please try again or email marcus@flippingfriends.co.uk
              </p>
            )}
            <p className="text-center text-zinc-400 text-sm flex items-center justify-center gap-2">
              🔒 We'll never spam you or share your email. Unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
